// Dashboard portale clienti — KPI cards, ultimi ticket, ultimi rapportini.
// Bottoni azione: apri ticket, prenota manutenzione.
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Ticket,
  CheckCircle,
  Calendar,
  TrendingUp,
  Plus,
  CalendarPlus,
  FileText,
  Download,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { StatusPill } from '../../components/ui/StatusPill'
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card'
import { Skeleton } from '../../components/ui/Skeleton'
import { portalApiCalls } from './portal.api'
import { formatDate, formatRelativeDate } from '../../lib/utils'
import type { TicketStatus } from '@fieldcore/types'

// ---------------------------------------------------------------
// Componente KPI Card
// ---------------------------------------------------------------

interface KPICardProps {
  label: string
  value: string | number
  icon: React.ReactNode
  iconBg: string
  description?: string
  highlight?: boolean
}

function KPICard({ label, value, icon, iconBg, description, highlight }: KPICardProps) {
  return (
    <div
      className={`rounded-xl border p-5 ${
        highlight
          ? 'border-blue-200 bg-blue-50'
          : 'border-gray-200 bg-white'
      } shadow-sm`}
      role="article"
      aria-label={`${label}: ${value}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="mb-1 text-sm text-gray-500">{label}</p>
          <p
            className={`text-2xl font-bold ${
              highlight ? 'text-[var(--fc-primary)]' : 'text-gray-900'
            }`}
          >
            {value}
          </p>
          {description && (
            <p className="mt-1 text-xs text-gray-400">{description}</p>
          )}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBg}`}>
          {icon}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------
// Pagina
// ---------------------------------------------------------------

export function PortalDashboardPage() {
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'dashboard'],
    queryFn: () => portalApiCalls.getDashboard().then((r) => r.data),
    refetchInterval: 60_000, // aggiorna ogni minuto
  })

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <AlertTriangle className="mb-3 h-10 w-10 text-amber-400" aria-hidden="true" />
        <h2 className="mb-2 text-lg font-semibold text-gray-900">
          Impossibile caricare la dashboard
        </h2>
        <p className="text-sm text-gray-500">
          Ricarica la pagina o contatta il supporto.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Intestazione */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Panoramica dei tuoi interventi e richieste
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => navigate('/portal/tickets/new')}
            leftIcon={<Plus className="h-4 w-4" />}
            size="sm"
          >
            Apri nuovo ticket
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate('/portal/calendar')}
            leftIcon={<CalendarPlus className="h-4 w-4" />}
            size="sm"
          >
            Prenota manutenzione
          </Button>
        </div>
      </div>

      {/* KPI Grid */}
      <section aria-label="Indicatori chiave">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={100} className="rounded-xl" />
            ))}
          </div>
        ) : data ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <KPICard
              label="Ticket aperti"
              value={data.openTickets}
              icon={<Ticket className="h-5 w-5 text-[var(--fc-primary)]" aria-hidden="true" />}
              iconBg="bg-blue-100"
              highlight={data.openTickets > 0}
              description="In lavorazione"
            />
            <KPICard
              label="Completati (mese)"
              value={data.completedThisMonth}
              icon={<CheckCircle className="h-5 w-5 text-[var(--fc-success)]" aria-hidden="true" />}
              iconBg="bg-green-100"
              description="Questo mese"
            />
            <KPICard
              label="Prossimo intervento"
              value={
                data.nextScheduledIntervention.date
                  ? formatDate(data.nextScheduledIntervention.date, 'dd/MM')
                  : 'Nessuno'
              }
              icon={<Calendar className="h-5 w-5 text-amber-600" aria-hidden="true" />}
              iconBg="bg-amber-100"
              description={data.nextScheduledIntervention.title ?? 'Nessun intervento pianificato'}
            />
            <KPICard
              label="SLA rispettato"
              value={`${data.slaRespectPercent}%`}
              icon={<TrendingUp className="h-5 w-5 text-purple-600" aria-hidden="true" />}
              iconBg="bg-purple-100"
              description="Ultimi 90 giorni"
              highlight={data.slaRespectPercent >= 95}
            />
          </div>
        ) : null}
      </section>

      {/* Sezioni tabelle */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Ultimi ticket */}
        <section aria-label="Ultimi ticket">
          <Card noPadding>
            <CardHeader className="border-b border-gray-100 px-5 pb-4 pt-5">
              <CardTitle>Ultimi ticket</CardTitle>
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-[var(--fc-primary)] hover:underline"
                onClick={() => navigate('/portal/tickets')}
              >
                Tutti
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </CardHeader>
            <CardContent className="divide-y divide-gray-50">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="px-5 py-3">
                    <Skeleton height={40} />
                  </div>
                ))
              ) : data?.recentTickets.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-gray-400">
                  Nessun ticket recente
                </div>
              ) : (
                data?.recentTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    className="flex w-full items-start justify-between px-5 py-3 text-left transition-colors hover:bg-gray-50"
                    onClick={() => navigate(`/portal/tickets/${ticket.id}`)}
                    aria-label={`Apri ticket ${ticket.ticketNumber}: ${ticket.title}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {ticket.title}
                      </p>
                      <p className="text-xs text-gray-400">
                        {ticket.ticketNumber} &middot; {formatRelativeDate(ticket.createdAt)}
                      </p>
                    </div>
                    <StatusPill
                      status={ticket.status as TicketStatus}
                      size="sm"
                      className="ml-3 flex-shrink-0"
                    />
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        </section>

        {/* Ultimi rapportini */}
        <section aria-label="Ultimi rapportini">
          <Card noPadding>
            <CardHeader className="border-b border-gray-100 px-5 pb-4 pt-5">
              <CardTitle>Rapportini recenti</CardTitle>
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-[var(--fc-primary)] hover:underline"
                onClick={() => navigate('/portal/reports')}
              >
                Tutti
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </CardHeader>
            <CardContent className="divide-y divide-gray-50">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="px-5 py-3">
                    <Skeleton height={40} />
                  </div>
                ))
              ) : data?.recentReports.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-gray-400">
                  Nessun rapportino disponibile
                </div>
              ) : (
                data?.recentReports.map((report) => (
                  <div
                    key={report.id}
                    className="flex items-start justify-between px-5 py-3"
                  >
                    <div className="flex items-start gap-3">
                      <FileText
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {report.reportNumber}
                        </p>
                        {report.workDescription && (
                          <p className="max-w-xs truncate text-xs text-gray-400">
                            {report.workDescription}
                          </p>
                        )}
                        <p className="text-xs text-gray-400">
                          {formatRelativeDate(report.createdAt)}
                        </p>
                      </div>
                    </div>
                    {report.pdfUrl && (
                      <a
                        href={report.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-3 flex flex-shrink-0 items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 transition-colors hover:border-[var(--fc-primary)] hover:text-[var(--fc-primary)]"
                        aria-label={`Scarica PDF rapportino ${report.reportNumber}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download className="h-3.5 w-3.5" aria-hidden="true" />
                        PDF
                      </a>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  )
}
